/**
 * Submission result — fetches GET /api/submissions/:id which includes
 * the per-problem grading rows.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';

interface ResultRow {
  id: string;
  problem_id: string;
  is_correct: boolean;
  student_answer: string | null;
  ai_feedback: string | null;
  z3_verified: boolean | null;
  z3_reason: string | null;
}

interface SubmissionDetail {
  id: string;
  assignment_id: string;
  status: string;
  total_score: number | null;
  error_message: string | null;
  results: ResultRow[];
}

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  done: { label: '채점 완료', cls: 'status-done' },
  grading: { label: '채점 중', cls: 'status-grading' },
  pending: { label: '대기 중', cls: 'status-pending' },
};

export function SubmissionResult() {
  const { submissionId } = useParams();
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!submissionId) return;
    let active = true;
    (async () => {
      try {
        const data = await api.get<{ submission: SubmissionDetail }>(
          `/api/submissions/${submissionId}`,
        );
        if (active) setSubmission(data.submission);
      } catch (e) {
        if (active) setError((e as Error).message);
      }
    })();
    return () => {
      active = false;
    };
  }, [submissionId]);

  if (error) {
    return (
      <main className="page-narrow">
        <p className="error-text" role="alert">오류: {error}</p>
      </main>
    );
  }
  if (!submission) {
    return (
      <main className="page-narrow">
        <div className="loading" role="status" aria-label="불러오는 중">
          <span>불러오는 중…</span>
        </div>
      </main>
    );
  }

  const statusInfo = STATUS_MAP[submission.status] ?? STATUS_MAP.pending;
  const correctCount = submission.results.filter((r) => r.is_correct).length;

  return (
    <main className="page-narrow">
      <Link to="/student" className="back-link">숙제 목록</Link>

      <div className="header no-border-bottom mb-4">
        <h1 data-testid="result-title">Grading Result</h1>
        <span className={`status ${statusInfo.cls}`} data-testid="result-status">
          {statusInfo.label}
        </span>
      </div>

      {submission.status === 'done' && submission.total_score != null && (
        <div data-testid="result-score" className="mb-8">
          <span className="score">
            <span className="score-number">{submission.total_score}</span>
            <span className="score-unit">점</span>
          </span>
          <span className="list-item-meta ml-3">
            ({correctCount}/{submission.results.length} 정답)
          </span>
        </div>
      )}

      {submission.error_message && (
        <p className="error-text mb-4" role="alert">
          {submission.error_message}
        </p>
      )}

      <ul className="list-stack" data-testid="result-list" aria-label="문제별 채점 결과">
        {submission.results.map((r, i) => (
          <li
            key={r.id}
            className={`result-card ${r.is_correct ? 'correct' : 'incorrect'}`}
            data-testid={`result-${i}`}
          >
            <div className={`result-badge ${r.is_correct ? 'correct' : 'incorrect'}`}>
              {r.is_correct ? '\u2713 정답' : '\u2717 오답'} — 문제 {i + 1}
            </div>
            <div className="list-item-body mt-2">
              학생 답: <code>{r.student_answer ?? '(없음)'}</code>
            </div>
            {r.z3_reason && (
              <div className="list-item-meta mt-1">
                {r.z3_reason}
              </div>
            )}
            {r.ai_feedback && (
              <div className="list-item-meta mt-2">
                {r.ai_feedback}
              </div>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
